import { ChatAttributeFilter, ChatAttributeFilters } from "@/components/ui/chat/chatFilterUtils";
import { create } from "@/lib/zustand";
import { AttributeFilters } from "@/models/attributeFilter";
import { Voc } from "@/models/Voc";
import { VocPeriodFilter, VocPeriodFilterType } from "@/models/vocPeriodFilter";

type SearchResult = {
  vocs: Voc[];
  total: number;
  keyword: string;
  categories: string[];
  startDate: Date | null;
  endDate: Date | null;
  attributeFilters?: AttributeFilters;
  tagIds?: string[];
};

type VocSearchState = {
  keyword: string;
  categories: string[];
  periodFilter: VocPeriodFilter;
  attributeFilters: ChatAttributeFilters;
  tagIds: string[];
  result?: SearchResult;
};

type VocSearchActions = {
  setKeyword: (keyword: string) => void;
  setCategories: (categories: string[]) => void;
  setSearchResults: (result: SearchResult) => void;
  updatePeriodFilter: (updates: Partial<VocPeriodFilter>) => void;
  updateAttributeFilter: (attributeName: string, filter: ChatAttributeFilter | null) => void;
  toggleTagId: (tagId: string) => void;
  clearFilters: () => void;
};

export type VocSearchStore = VocSearchState & VocSearchActions;

const initialState: VocSearchState = {
  keyword: "",
  categories: [],
  periodFilter: {
    type: VocPeriodFilterType.ALL,
    start: null,
    end: null,
  },
  attributeFilters: {},
  tagIds: [],
  result: undefined,
};

const removeUndefinedValues = <T extends Record<string, unknown>>(
  obj: Partial<T>,
): Partial<T> => {
  const entries = Object.entries(obj).filter(([_, value]) => value !== undefined);
  return Object.fromEntries(entries) as Partial<T>;
};

const createVocSearchStore = () =>
  create<VocSearchStore>()((set) => ({
    ...initialState,

    setKeyword: (keyword) => set({ keyword }),
    setCategories: (categories) => set({ categories }),
    setSearchResults: (result) => set({ result }),
    updatePeriodFilter: (updates) =>
      set((state) => {
        const cleanedUpdates = removeUndefinedValues(updates);
        return {
          periodFilter: { ...state.periodFilter, ...cleanedUpdates },
        };
      }),
    updateAttributeFilter: (attributeName, filter) =>
      set((state) => {
        if (filter) {
          return {
            attributeFilters: { ...state.attributeFilters, [attributeName]: filter },
          };
        } else {
          const { [attributeName]: _, ...rest } = state.attributeFilters;
          return { attributeFilters: rest };
        }
      }),
    toggleTagId: (tagId) =>
      set((state) => {
        const isSelected = state.tagIds.includes(tagId);
        return {
          tagIds: isSelected
            ? state.tagIds.filter((id) => id !== tagId)
            : [...state.tagIds, tagId],
        };
      }),
    clearFilters: () =>
      set({
        categories: initialState.categories,
        periodFilter: initialState.periodFilter,
        attributeFilters: initialState.attributeFilters,
        tagIds: initialState.tagIds,
      }),
  }));

const vocSearchStores = new Map<string, ReturnType<typeof createVocSearchStore>>();

export const getVocSearchStore = (workspaceId: string) => {
  if (!vocSearchStores.has(workspaceId)) {
    vocSearchStores.set(workspaceId, createVocSearchStore());
  }
  return vocSearchStores.get(workspaceId)!;
};
